import {seamlessBootstrap, seamlessImmutable} from 'epic-alt-utils';

import COUNTRY_REGION_MAPPING from '../constants/PsnContryRegionMapping';
import Immutable from 'seamless-immutable';
import PosaRedeemActions from '../actions/PosaRedeemActions';
import PosaRedeemSource from '../sources/PosaRedeemSource';
import {alt} from 'epic-react-common';
import {datasource} from 'alt-utils/lib/decorators';
import epicTracking from 'epic-tracking';
import {getPlatformShortToLong} from '../utils/index';

@datasource(PosaRedeemSource)
@seamlessImmutable
class PosaRedeemStore {
  constructor() {
    this.on('error', (error) => {
      console.error(error);
    });
    this.on('bootstrap', seamlessBootstrap.bind(this, null));
    this.state = Immutable({
      loading: false,
      page: null,
      error: false,
      errorCode: null,
      code: null,
      codeVerified: false,
      verifying: false,
      platform: null,
      psnCountry: null,
      psnRegion: null,
      showConfirm: false,
      epicAccountName: null,
      redeemed: false,
      secondaryCode: {},
    });
    this.bindActions(PosaRedeemActions);
  }

  trackEvent(action, data = {}) {
    const {platform, psnCountry, psnRegion} = this.state;
    const track = {
      interactionType: 'api',
      eventCategory: 'PosaRedeem',
      eventAction: action,
    };
    if (platform) track.platform = platform;
    if (psnCountry) track.psnCountry = psnCountry;
    if (psnRegion) track.psnRegion = psnRegion;
    epicTracking.trackEvent('Interaction', {
      ...track,
      ...data,
    });
  }

  onTrack(args) {
    this.trackEvent(...args);
  }

  onGetCmsContent() {
    this.getInstance().getCmsContent();
    this.setState(
      this.state.merge({
        loading: true,
      })
    );
  }

  onGetCmsContentSuccess({data}) {
    if (data.success) {
      this.setState(
        this.state.merge({
          loading: false,
          error: false,
          page: data.page,
        })
      );
    } else {
      this.setState(
        this.state.merge({
          loading: false,
          error: true,
        })
      );
    }
  }

  onGetCmsContentFailture() {
    this.setState(
      this.state.merge({
        loading: false,
        error: true,
      })
    );
  }

  onValidateCode(code) {
    const pureCode = code.trim();
    this.trackEvent('validateCode', {interactionType: 'click'});
    this.getInstance().validateCode(pureCode);
    this.setState(
      this.state.merge({
        verifying: true,
        errorCode: null,
      })
    );
  }

  onValidateCodeSuccess({data}) {
    if (data.success) {
      const {
        offer,
        externalAuths,
        wallets,
        code,
        epicAccountName,
        connectedPlatforms,
        bonusOffers,
      } = data;
      this.trackEvent('codeVerifySuccess');
      this.setState(
        this.state.merge({
          code,
          verifying: false,
          codeVerified: true,
          error: false,
          offer,
          externalAuths,
          wallets,
          epicAccountName,
          connectedPlatforms,
          bonusOffers,
        })
      );
    } else {
      this.trackEvent('codeVerifySuccess', {errorCode: data.errorCode});
      this.setState(
        this.state.merge({
          code: null,
          verifying: false,
          codeVerified: false,
          error: true,
          errorCode: data.errorCode,
        })
      );
    }
  }

  onValidateCodeFailure({response}) {
    let errorCode = null;
    try {
      errorCode = response.data.errorCode || 'errors.com.epicgames.posa.unknown';
    } catch (ex) {
      errorCode = 'errors.com.epicgames.posa.unknown';
    }
    this.trackEvent('codeVerifyFailure', {errorCode});
    this.setState(
      this.state.merge({
        code: null,
        verifying: false,
        codeVerified: false,
        error: true,
        errorCode,
      })
    );
  }

  onRedeemCode() {
    const {platform, code, psnRegion} = this.state;
    const pureCode = code.trim();
    const platformValue = getPlatformShortToLong(platform);
    const paylaod = {
      platform: platformValue,
    };
    if (platformValue === 'PSN') {
      paylaod.platformRegion = psnRegion;
    }
    this.trackEvent('codeRedeemSuccess', {interactionType: 'click'});
    this.getInstance().redeemCode(pureCode, paylaod);
    this.setState(
      this.state.merge({
        loading: true,
        error: null,
        errorCode: null,
      })
    );
  }

  onRedeemCodeSuccess({data}) {
    if (data.success) {
      const {secondaryCode} = data;
      this.trackEvent('codeRedeemSuccess', {
        secondaryCodePlatform: secondaryCode && secondaryCode.platform,
      });
      this.setState(
        this.state.merge({
          redeemed: true,
          loading: false,
          secondaryCode,
        })
      );
    } else {
      this.trackEvent('codeRedeemFailure', {errorCode: 'unknown'});
      this.setState(
        this.state.merge({
          redeemed: false,
          loading: false,
        })
      );
    }
  }

  onRedeemCodeFailure({response}) {
    let errorCode = null;
    try {
      errorCode = response.data.errorCode || 'errors.com.epicgames.posa.unknown';
    } catch (ex) {
      errorCode = 'errors.com.epicgames.posa.unknown';
    }
    this.trackEvent('codeRedeemFailure', {errorCode});
    this.setState(
      this.state.merge({
        redeemed: false,
        errorCode,
        loading: false,
      })
    );
  }

  // Called to clear error code when a valid code is entered.
  resetErrorCode() {
    this.setState(
      this.state.merge({
        errorCode: null,
      })
    );
  }

  // Called when a player is starting flow over.
  resetFlow() {
    this.trackEvent('startOver', {interactionType: 'click', redeemed: this.state.redeemed});
    this.setState(
      this.state.merge({
        error: false,
        errorCode: null,
        code: null,
        codeVerified: false,
        verifying: false,
        platform: null,
        psnCountry: null,
        showConfirm: false,
        epicAccountName: null,
        redeemed: null,
      })
    );
  }

  setPlatform(platform) {
    this.trackEvent('selectPlatform', {interactionType: 'click', platform});
    this.setState(
      this.state.merge({
        platform,
      })
    );
  }

  setShowConfirm(showConfirm) {
    this.trackEvent(showConfirm ? 'showConfirmOverlay' : 'closeConfirmOverlay');
    this.setState(
      this.state.merge({
        showConfirm,
      })
    );
  }

  setPsnCountry(country) {
    this.setState(
      this.state.merge({
        psnCountry: country,
        psnRegion: COUNTRY_REGION_MAPPING[country],
      })
    );
  }
}

export default alt.createStore(PosaRedeemStore, 'PosaRedeemStore');
